import React, { useState } from "react";
import { useContent } from "../context/ContentContext";
import { ArrowUpRight } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import SEO from "./SEO";

const Contact = () => {
  const { BRAND, DISCIPLINES } = useContent();
  const { toast } = useToast();
  const [mode, setMode] = useState("client");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    discipline: "",
    portfolio: "",
    message: "",
  });

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const isClient = mode === "client";

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    // Local fallback: never lose a submission even if the network fails.
    const store = JSON.parse(localStorage.getItem("samurai_leads") || "[]");
    store.push({ ...form, mode, ts: Date.now() });
    localStorage.setItem("samurai_leads", JSON.stringify(store));

    const payload = {
      name: form.name,
      email: form.email,
      mode,
      company: isClient ? form.company : "",
      phone: isClient ? form.phone : "",
      portfolio: !isClient ? form.portfolio : "",
      discipline: !isClient ? form.discipline : "",
      message: form.message,
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      const ackOk = data?.delivery?.ack;
      toast({
        title: isClient ? "Brief received." : "Application received.",
        description: ackOk
          ? `We just sent you a confirmation. The studio will reply from ${BRAND.email} within 48 hours.`
          : `We've got your ${isClient ? "brief" : "application"}. The studio will reply from ${BRAND.email} within 48 hours.`,
      });
      setForm({
        name: "",
        email: "",
        company: "",
        discipline: "",
        portfolio: "",
        message: "",
      });
    } catch (err) {
      toast({
        title: "We couldn't send that right now.",
        description: `Please email ${BRAND.email} directly — we saved a local copy of your brief.`,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-canvas text-ink min-h-screen">
      <SEO
        title="Contact SAMURAI · Let's Craft Your Idea"
        description="Get in touch with SAMURAI in Mumbai or Goa. Tell us about your film, campaign or content engine and our studio will respond within 48 hours."
        path="/contact"
      />
      {/* Slim hero — headline only. Always dark-treated so the page-top header
          matches the other pages: white text on matte-black regardless of theme. */}
      <section className="dark-block bg-canvas pt-32 pb-10 border-b border-ink/10">
        <div className="mx-auto px-6 md:px-10">
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-4">
            Contact
          </p>
          <h1 className="font-heading font-black text-[36px] md:text-[56px] leading-[1.02] tracking-[-0.03em] max-w-4xl">
            {isClient ? (
              <>
                Let’s craft your{" "}
                <span className="italic font-serif text-ink/70">idea.</span>
              </>
            ) : (
              <>
                Join the{" "}
                <span className="italic font-serif text-ink/70">
                  collective.
                </span>
              </>
            )}
          </h1>
          <p
            className="mt-6 max-w-2xl text-sm md:text-base text-ink/70 leading-[1.65] font-light"
            data-testid="contact-lede"
            style={{ fontFamily: "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif" }}
          >
            SAMURAI is a Mumbai and Goa based creative studio building brand
            films, print campaigns and custom content engines that pair
            live-action craft with generative AI. Send us a script, a napkin
            sketch, a moodboard or a full brief — the studio replies from{" "}
            <span className="text-ink">{BRAND.email}</span> within 48 working
            hours. Artists, directors, DoPs, editors and AI specialists can
            apply to the collective from the same form.
          </p>
        </div>
      </section>

      {/* Two-panel contact block, styled from the reference */}
      <section className="py-14 md:py-20">
        <div className="mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
            {/* LEFT PANEL — toggle + description (centered) */}
            <div
              className="lg:col-span-5 bg-surface border border-ink/10 rounded-2xl p-6 md:p-10 flex flex-col items-center justify-center text-center min-h-[280px]"
              data-testid="contact-intent-panel"
              style={{
                fontFamily:
                  "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif",
              }}
            >
              {/* Segmented pill toggle inside a track */}
              <div
                className="relative inline-flex bg-canvas rounded-full p-1 border border-ink/10"
                data-testid="contact-mode-toggle"
              >
                <button
                  type="button"
                  onClick={() => setMode("client")}
                  className={`relative z-10 px-5 md:px-7 py-2.5 rounded-full text-[10px] md:text-[11px] uppercase tracking-[0.28em] transition-colors ${
                    isClient
                      ? "bg-ink text-canvas"
                      : "text-ink/70 hover:text-ink"
                  }`}
                  data-testid="contact-mode-client"
                >
                  For Clients
                </button>
                <button
                  type="button"
                  onClick={() => setMode("artist")}
                  className={`relative z-10 px-5 md:px-7 py-2.5 rounded-full text-[10px] md:text-[11px] uppercase tracking-[0.28em] transition-colors ${
                    !isClient
                      ? "bg-ink text-canvas"
                      : "text-ink/70 hover:text-ink"
                  }`}
                  data-testid="contact-mode-artist"
                >
                  Apply as an Artist
                </button>
              </div>

              {/* Description — matches About body: Geist, text-sm md:text-base */}
              <p
                className="mt-8 md:mt-10 text-sm md:text-base leading-[1.55] font-light text-ink/85 tracking-[-0.005em] max-w-md mx-auto"
                data-testid="contact-intent-copy"
              >
                {isClient
                  ? "Tell us what you have in mind, a script, a brief or a napkin sketch. We’ll take it from there."
                  : "Show us your work. We collaborate with directors, DoPs, editors, AI artists and writers year round."}
              </p>
            </div>

            {/* RIGHT PANEL — form */}
            <div
              className="lg:col-span-7 bg-surface border border-ink/10 rounded-2xl p-6 md:p-10"
              data-testid="contact-form-panel"
              style={{
                fontFamily:
                  "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif",
              }}
            >
              <form
                onSubmit={submit}
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
                data-testid="contact-form"
              >
                <FormField
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />

                {isClient ? (
                  <>
                    <div className="md:col-span-2">
                      <FormField
                        label="Company / Brand"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <FormField
                        label="Phone"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </>
                ) : (
                  <div className="md:col-span-2">
                    <FormField
                      label="Portfolio / Reel link"
                      name="portfolio"
                      value={form.portfolio}
                      onChange={handleChange}
                    />
                  </div>
                )}

                {!isClient && (
                  <div className="md:col-span-2">
                    <label className="block text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-3">
                      Discipline
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {DISCIPLINES.map((d) => (
                        <button
                          type="button"
                          key={d}
                          onClick={() => setForm({ ...form, discipline: d })}
                          className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                            form.discipline === d
                              ? "bg-ink text-canvas border-ink"
                              : "border-ink/30 text-ink/70 hover:border-ink"
                          }`}
                          data-testid={`contact-discipline-${d.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="block text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-3">
                    {isClient
                      ? "Tell us what you have in mind"
                      : "About you"}
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full bg-transparent border-b border-ink/25 focus:border-ink outline-none py-2 text-ink resize-none transition-colors text-base"
                    data-testid="contact-message-input"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center gap-4 disabled:opacity-60"
                    data-testid="contact-send-btn"
                  >
                    <span className="text-[11px] uppercase tracking-[0.32em]">
                      {submitting ? "Sending" : "Send"}
                    </span>
                    <span className="w-11 h-11 rounded-full bg-ink text-canvas flex items-center justify-center transition-transform duration-500 group-hover:rotate-45">
                      <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const FormField = ({ label, name, value, onChange, type = "text", required = false }) => (
  <div>
    <label className="block text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-3">
      {label}
    </label>
    <input
      required={required}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      className="w-full bg-transparent border-b border-ink/25 focus:border-ink outline-none py-2 text-ink transition-colors text-base"
      data-testid={`contact-${name}-input`}
    />
  </div>
);

export default Contact;
